import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Trash2, ShieldAlert, CheckSquare, Square, AlertTriangle, UserCheck } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { helpdeskTicketsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';

const STATUS_LABELS = {
  open: 'Abierto',
  in_progress: 'En proceso',
  on_hold: 'En espera',
  scheduled: 'Programado',
  closed: 'Cerrado',
};

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  on_hold: 'bg-slate-100 text-slate-700',
  scheduled: 'bg-purple-100 text-purple-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

const TicketCleanupPanel = ({ tickets: allTickets, onDeleted, canDelete = true, canAssign = false, agents = [] }) => {
  // Sin permiso de borrado (Admin no Master): solo se gestionan tickets cerrados
  const tickets = canDelete ? allTickets : allTickets.filter(t => t.status === 'closed');
  const [isOpen, setIsOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAgent, setAssignAgent] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAll = () => setSelected(new Set(tickets.map(t => t.id)));
  const deselectAll = () => setSelected(new Set());

  const handleDelete = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const res = await helpdeskTicketsAPI.bulkDeleteTickets(Array.from(selected));
      toast.success(`${res.data.deleted_tickets} ticket(s) eliminados permanentemente`);
      setSelected(new Set());
      setConfirmOpen(false);
      setIsOpen(false);
      onDeleted();
    } catch (error) {
      toast.error('Error al eliminar tickets');
    } finally {
      setDeleting(false);
    }
  };

  const selectedClosedIds = tickets.filter(t => selected.has(t.id) && t.status === 'closed').map(t => t.id);

  const handleAssign = async () => {
    if (!assignAgent || selectedClosedIds.length === 0) return;
    setAssigning(true);
    try {
      const res = await helpdeskTicketsAPI.bulkAssignClosed(selectedClosedIds, assignAgent === 'unassigned' ? null : assignAgent);
      toast.success(`${res.data.updated} ticket(s) reasignados`);
      setSelected(new Set());
      setAssignOpen(false);
      setAssignAgent('');
      onDeleted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar agente');
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
        data-testid="open-cleanup-panel"
      >
        <ShieldAlert className="w-4 h-4 mr-2" />
        Gestión de Datos
      </Button>
    );
  }

  return (
    <>
      <Card className="border-rose-200" data-testid="cleanup-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-rose-500" />
              {canDelete ? 'Gestión de Datos — Admin Master' : 'Gestión de Datos'}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => { setIsOpen(false); deselectAll(); }}>
              Cerrar
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {canDelete
              ? 'Selecciona los tickets que deseas eliminar permanentemente. Esta acción no se puede deshacer.'
              : 'Selecciona tickets cerrados para asignarles un agente.'}
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Actions bar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                Seleccionar todos
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">
                <Square className="w-3.5 h-3.5 mr-1.5" />
                Deseleccionar todos
              </Button>
              <span className="text-sm text-slate-500 ml-2">
                {selected.size} de {tickets.length} seleccionados
              </span>
            </div>
            <div className="flex items-center gap-2">
              {canAssign && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selectedClosedIds.length === 0}
                  onClick={() => setAssignOpen(true)}
                  data-testid="bulk-assign-agent-btn"
                >
                  <UserCheck className="w-3.5 h-3.5 mr-1.5" />
                  Asignar agente ({selectedClosedIds.length})
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={selected.size === 0}
                  onClick={() => setConfirmOpen(true)}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Eliminar seleccionados ({selected.size})
                </Button>
              )}
            </div>
          </div>

          {/* Ticket list */}
          {tickets.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              No hay tickets registrados.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-left text-xs text-slate-500 uppercase">
                    <th className="p-3 w-10"></th>
                    <th className="p-3">Folio</th>
                    <th className="p-3">Título</th>
                    <th className="p-3">Estatus</th>
                    <th className="p-3">Fecha</th>
                    <th className="p-3">Solicitante</th>
                    <th className="p-3">Asignado a</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(ticket => (
                    <tr
                      key={ticket.id}
                      className={`border-t hover:bg-slate-50 transition-colors ${selected.has(ticket.id) ? 'bg-rose-50' : ''}`}
                      data-testid={`cleanup-ticket-${ticket.id}`}
                    >
                      <td className="p-3">
                        <Checkbox
                          checked={selected.has(ticket.id)}
                          onCheckedChange={() => toggleSelect(ticket.id)}
                          data-testid={`checkbox-${ticket.id}`}
                        />
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-600">
                        TK-{ticket.folio || ticket.id.slice(0, 8)}
                      </td>
                      <td className="p-3 font-medium text-slate-700 max-w-[250px] truncate">
                        {ticket.title}
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={STATUS_COLORS[ticket.status] || ''}>
                          {STATUS_LABELS[ticket.status] || ticket.status}
                        </Badge>
                      </td>
                      <td className="p-3 text-slate-500 text-xs whitespace-nowrap">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        {ticket.requester_name || ticket.created_by_name || '—'}
                      </td>
                      <td className="p-3 text-slate-600 text-xs">
                        {ticket.assigned_to_name || 'Sin asignar'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription>
              Estás a punto de eliminar <strong>{selected.size} ticket(s)</strong> de forma permanente, 
              incluyendo todos sus comentarios e historial.
              <br /><br />
              <strong>Esta acción no se puede deshacer.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Eliminando...' : `Sí, eliminar ${selected.size} ticket(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk assign agent (closed tickets, Admin only) */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Asignar agente a tickets cerrados
            </DialogTitle>
            <DialogDescription data-testid="bulk-assign-count">
              Se modificarán <strong>{selectedClosedIds.length} ticket(s) cerrado(s)</strong>. Solo cambia el agente asignado:
              el estado, las fechas, el SLA y el CSAT no se alteran y no se envían notificaciones.
            </DialogDescription>
          </DialogHeader>
          <Select value={assignAgent} onValueChange={setAssignAgent}>
            <SelectTrigger data-testid="bulk-assign-agent-select">
              <SelectValue placeholder="Selecciona un agente" />
            </SelectTrigger>
            <SelectContent>
              {agents.map(a => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
              <SelectItem value="unassigned">Sin asignar</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAssignOpen(false)} disabled={assigning}>
              Cancelar
            </Button>
            <Button onClick={handleAssign} disabled={assigning || !assignAgent} data-testid="confirm-bulk-assign-btn">
              {assigning ? 'Asignando...' : `Sí, asignar a ${selectedClosedIds.length} ticket(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketCleanupPanel;
